/**
 * Covermatic Studio form and upload controllers.
 */
(() => {
  const runtime = window.CovermaticRuntime || window.AlphaRuntime || {};
  window.CovermaticRuntime = runtime;
  window.AlphaRuntime = runtime;
  const {
    Config,
    State,
    Utils,
    ApiClient,
    Events,
    StorageKeys,
    DEFAULT_PREMADE_CONCEPT,
    normalizeTemplateSelection,
    shouldSendTemplateImageInput,
    buildGenerationConcept,
  } = runtime;
  const getService = (name) => runtime.getService?.(name);
  const TaskManager = new Proxy({}, {
    get(_target, prop) { return runtime.TaskManager?.[prop]; }
  });

  class FileUploader {
    constructor(zoneId, inputId, previewId, maxFiles = Config.maxUploadFiles || 10) {
      this.zone = Utils.$(zoneId);
      this.input = Utils.$(inputId);
      this.previewWrap = Utils.$(previewId);
      this.previewGrid = this.previewWrap ? this.previewWrap.querySelector('div[id$="-grid"]') : null;
      this.uploadedUrls = [];
      this.maxFiles = maxFiles;
      this.maxFileSizeBytes = Config.maxUploadFileSizeBytes || (20 * 1024 * 1024);
      this.isUploading = false;

      if (this.zone) this.init();
    }

    setUploading(isUploading) {
      if (this.isUploading === isUploading) return;
      this.isUploading = isUploading;
      State.activeUploads = Math.max(0, Number(State.activeUploads || 0) + (isUploading ? 1 : -1));
    }

    init() {
      const handleSelectedFiles = (files) => this.handleFiles(files);

      this.zone.addEventListener('click', () => {
        if (!this.checkAuth()) return;
        this.input?.click();
      });

      this.zone.addEventListener('dragover', (event) => {
        event.preventDefault();
        if (this.checkAuth(false)) this.zone.classList.add('border-brand-400');
      });

      this.zone.addEventListener('dragleave', () => this.zone.classList.remove('border-brand-400'));
      this.zone.addEventListener('drop', (event) => {
        event.preventDefault();
        this.zone.classList.remove('border-brand-400');
        if (this.checkAuth()) handleSelectedFiles(event.dataTransfer.files);
      });

      if (this.input) {
        this.input.addEventListener('change', () => {
          if (this.checkAuth()) handleSelectedFiles(this.input.files);
          this.input.value = '';
        });
      }

      const removeAllBtn = this.previewWrap?.querySelector('button[id*="remove-all"]');
      if (removeAllBtn) removeAllBtn.addEventListener('click', () => this.reset());
    }

    checkAuth(showToast = true) {
      if (Config.toolSlug === 'photomatic') {
        if (!Utils.isLoggedIn()) {
          if (showToast) Utils.toast('You can choose a photo now. Sign in on the final step to create.', 'warn');
          return true;
        }
        return true;
      }

      if (!Utils.isLoggedIn() || State.credits === 0) {
        if (showToast) {
          Utils.toast('Credits required for photo upload', 'warn');
          if (State.credits === 0) Events.emit('modal:buy-open-requested');
        }
        return false;
      }
      return true;
    }

    async handleFiles(fileList) {
      if (!fileList || !fileList.length || !this.previewWrap || !this.previewGrid) return;

      const files = Array.from(fileList);
      if (this.uploadedUrls.length + files.length > this.maxFiles) {
        Utils.toast(`Limit reached. Max ${this.maxFiles} files.`, 'error');
        return;
      }

      const validFiles = files.filter((file) => file.type.startsWith('image/') && file.size <= this.maxFileSizeBytes);
      if (!validFiles.length) {
        Utils.toast('No valid images to upload', 'error');
        return;
      }

      Utils.toast(`Uploading ${files.length} photo(s)...`);
      this.previewWrap.classList.remove('hidden');
      this.setUploading(true);

      try {
        for (const file of files) {
          if (!file.type.startsWith('image/') || file.size > this.maxFileSizeBytes) continue;

          try {
            const localUrl = URL.createObjectURL(file);
            const ui = this.createPreviewUI(localUrl);
            this.previewGrid.appendChild(ui.el);

            if (Config.toolSlug === 'photomatic' && !Utils.isLoggedIn()) {
              ui.removeBtn.addEventListener('click', () => {
                ui.el.remove();
                if (!this.previewGrid?.children.length) this.previewWrap.classList.add('hidden');
              });
              continue;
            }

            const data = await ApiClient.getUploadUrl(file.type);
            await ApiClient.uploadFile(data.upload_url, file);
            this.uploadedUrls.push(data.download_url);

            ui.removeBtn.addEventListener('click', () => {
              const idx = this.uploadedUrls.indexOf(data.download_url);
              if (idx > -1) this.uploadedUrls.splice(idx, 1);
              ui.el.remove();
              if (this.uploadedUrls.length === 0) this.previewWrap.classList.add('hidden');
            });
          } catch (error) {
            console.error(error);
            Utils.toast('Upload failed for one or more files', 'error');
          }
        }
      } finally {
        this.setUploading(false);
      }

      if (this.uploadedUrls.length > 0) Utils.toast('Upload complete');
    }

    createPreviewUI(src) {
      const el = document.createElement('div');
      el.className = 'relative group aspect-square';
      el.innerHTML = `
        <img src="${src}" class="w-full h-full rounded-lg object-cover border" loading="lazy" decoding="async" />
        <button type="button" class="absolute top-1 right-1 bg-red-600 text-white rounded-full w-5 h-5 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs shadow-sm">&times;</button>
      `;
      return { el, removeBtn: el.querySelector('button') };
    }

    reset() {
      this.uploadedUrls = [];
      if (this.previewGrid) this.previewGrid.innerHTML = '';
      if (this.previewWrap) this.previewWrap.classList.add('hidden');
    }
  }

  class FlowController {
    constructor() {
      this.els = {
        chooser: Utils.$('startingPoint'),
        formPanel: Utils.$('formPanel'),
        creativeLabPanel: Utils.$('creativeLabPanel'),
      };
      this.init();
    }

    init() {
      if (!this.els.chooser && !this.els.formPanel) return;

      document.addEventListener('click', (event) => {
        const choiceBtn = event.target.closest('[data-entry-choice]');
        if (choiceBtn) {
          if (choiceBtn.dataset.entryChoice === 'custom') this.startCustom();
          if (choiceBtn.dataset.entryChoice === 'free-cover-art') this.startFreeCoverArt();
          if (choiceBtn.dataset.entryChoice === 'covers') this.openSignatureCoverPicker();
          if (choiceBtn.dataset.entryChoice === 'styles') this.openStylePicker();
          if (choiceBtn.dataset.entryChoice === 'promo-graphics') this.openPromoGraphicsPicker();
          if (choiceBtn.dataset.entryChoice === 'creative-lab') this.startCreativeLab();
        }

        if (event.target.closest('#go-home') || event.target.closest('#creative-lab-go-home')) this.showChooser();
      });
    }

    saveFlowState() {
      const form = getService('form');
      if (form && typeof form.save === 'function') form.save();
    }

    scrollToForm() {
      const form = document.getElementById('cm-form');
      if (form) form.scrollIntoView();
    }

    showFormPanel({ scroll = false } = {}) {
      this.els.chooser?.classList.add('hidden');
      this.els.formPanel?.classList.remove('hidden');
      this.els.creativeLabPanel?.classList.add('hidden');
      if (scroll) this.scrollToForm();
    }

    showCreativeLabPanel() {
      this.els.chooser?.classList.add('hidden');
      this.els.formPanel?.classList.add('hidden');
      this.els.creativeLabPanel?.classList.remove('hidden');
    }

    showChooser() {
      State.entryChoice = null;
      this.els.chooser?.classList.remove('hidden');
      this.els.formPanel?.classList.add('hidden');
       this.els.creativeLabPanel?.classList.add('hidden');
      const modals = getService('modals');
      if (modals && typeof modals.closeStudioModal === 'function') modals.closeStudioModal();
      this.saveFlowState();
    }

    openSignatureCoverPicker({ isEditMode = false } = {}) {
      this.saveFlowState();
      const modals = getService('modals');
      if (modals && typeof modals.openTemplateModal === 'function') modals.openTemplateModal(isEditMode);
    }

    openStylePicker({ isEditMode = false } = {}) {
      State.entryChoice = 'styles';
      this.saveFlowState();
      const modals = getService('modals');
      if (modals && typeof modals.openStudioModal === 'function') modals.openStudioModal(isEditMode, 'style');
    }

    startCustom({ scroll = false } = {}) {
      State.entryChoice = 'custom';
      this.showFormPanel({ scroll });
      getService('form')?.syncFreeModeUi?.();
      runtime.App?.syncUI?.();
      this.saveFlowState();
    }

    startFreeCoverArt({ scroll = false } = {}) {
      State.entryChoice = 'free-cover-art';
      State.template = null;
      getService('genUploader')?.reset?.();
      getService('form')?.clearTemplate?.();
      this.showFormPanel({ scroll });
      getService('form')?.syncFreeModeUi?.();
      runtime.App?.syncUI?.();
      this.saveFlowState();
    }

    startCreativeLab() {
      State.entryChoice = 'creative-lab';
      this.showCreativeLabPanel();
      this.saveFlowState();
    }

    openPromoGraphicsPicker() {
      State.entryChoice = 'promo-graphics';
      State.pendingPromoGraphicAsset = null;
      const modals = getService('modals');
      if (modals && typeof modals.openPromoGraphicsModal === 'function') modals.openPromoGraphicsModal();
    }
    showFormFromSelection({ scroll = false } = {}) {
      if (!State.entryChoice) State.entryChoice = 'custom';
      this.showFormPanel({ scroll });
      this.saveFlowState();
    }

    restoreFromState({ hasFieldValue = false } = {}) {
      if (!this.els.formPanel) return;

      if (State.entryChoice === 'styles' && !State.template && !hasFieldValue) {
        this.els.chooser?.classList.remove('hidden');
        this.els.formPanel?.classList.add('hidden');
        this.els.creativeLabPanel?.classList.add('hidden');
        return;
      }

      if (State.entryChoice === 'promo-graphics') {
        State.entryChoice = null;
        this.els.chooser?.classList.remove('hidden');
        this.els.formPanel?.classList.add('hidden');
        this.els.creativeLabPanel?.classList.add('hidden');
        return;
      }

      if (State.entryChoice === 'creative-lab') {
        this.showCreativeLabPanel();
        return;
      }

      const shouldShowForm = Boolean(State.template || State.entryChoice === 'custom' || State.entryChoice === 'free-cover-art' || hasFieldValue);
      if (shouldShowForm) {
        if (!State.entryChoice) State.entryChoice = 'custom';
        this.showFormPanel();
        return;
      }

      this.els.chooser?.classList.remove('hidden');
      this.els.formPanel?.classList.add('hidden');
      this.els.creativeLabPanel?.classList.add('hidden');
    }
  }

  class FormController {
    constructor() {
      this.els = {
        artist: Utils.$('#artist'),
        title: Utils.$('title'),
        qty: Utils.$('#quantity'),
        concept: Utils.$('concept'),
        btn: Utils.$('generateButton'),
        error: Utils.$('generateError'),
        loading: Utils.$('generateLoading'),
        tplSelection: Utils.$('selectedTemplate'),
        freeStatus: Utils.$('#free-generation-status'),
        freeStatusTitle: Utils.$('#free-generation-status-title'),
        freeStatusCopy: Utils.$('#free-generation-status-copy'),
        freeReplenish: Utils.$('#free-generation-replenish'),
      };
      this.autosaveKey = StorageKeys.autosave;
      this.freeStatusTimer = null;
      this.freeQuota = null;
      this.init();
    }

    init() {
      this.restore();

      ['input', 'change'].forEach((eventName) => {
        Object.values(this.els).forEach((el) => {
          if (el && el.tagName !== 'DIV') el.addEventListener(eventName, () => this.save());
        });
      });

      if (this.els.btn) {
        this.els.btn.addEventListener('click', (event) => {
          event.preventDefault();
          this.submit();
        });
      }

      if (this.els.freeReplenish) {
        this.els.freeReplenish.addEventListener('click', async (event) => {
          event.preventDefault();
          await this.replenishFreeGenerations();
        });
      }

      document.addEventListener('click', (event) => {
        if (event.target.closest('#remove-template')) this.clearTemplate();
      });
      Events.on('free-generation:refresh-requested', () => {
        if (State.entryChoice === 'free-cover-art') this.refreshFreeGenerationStatus();
      });
      this.syncFreeModeUi();
    }

    save() {
      const data = {};
      ['artist', 'title', 'concept'].forEach((key) => {
        if (this.els[key]) data[key] = this.els[key].value;
      });

      if (State.entryChoice && State.entryChoice !== 'promo-graphics') data.flow = State.entryChoice;
      const normalizedTemplate = normalizeTemplateSelection(State.template);
      if (normalizedTemplate) data.template = normalizedTemplate;

      Utils.writeStorage(this.autosaveKey, JSON.stringify(data));
    }

    restore() {
      try {
        const data = JSON.parse(Utils.readStorage(this.autosaveKey, 'null'));
        const flow = getService('flow');
        if (!data) {
          flow?.showChooser?.();
          return;
        }

        let hasFieldValue = false;
        ['artist', 'title', 'concept'].forEach((key) => {
          if (this.els[key] && typeof data[key] === 'string') {
            this.els[key].value = data[key];
            if (data[key].trim()) hasFieldValue = true;
          }
        });

        if (this.els.concept && this.els.concept.value.trim() === DEFAULT_PREMADE_CONCEPT) {
          this.els.concept.value = '';
          hasFieldValue = false;
        }

        if (data.flow) State.entryChoice = data.flow;
        if (data.template && data.template.imageUrl) this.applyTemplate(data.template);

        if (State.entryChoice === 'promo-graphics') {
          State.entryChoice = null;
          flow?.showChooser?.();
        } else if (State.entryChoice === 'creative-lab') {
          flow?.showCreativeLabPanel?.();
        } else {
          State.entryChoice = null;
          flow?.showChooser?.();
        }
      } catch (error) {
        console.warn('Failed to restore Covermatic form state', error);
      }
    }

    applyTemplate(template) {
      const normalizedTemplate = normalizeTemplateSelection(template);
      if (!normalizedTemplate) return;

      State.template = normalizedTemplate;

      const thumb = Utils.$('templateThumb');
      const title = Utils.$('templateTitle');
      if (thumb) thumb.src = State.template.imageUrl;
      if (title) title.textContent = State.template.title || 'Selected direction';
      this.els.tplSelection?.classList.remove('hidden');
    }

    clearTemplate() {
      State.template = null;
      const thumb = Utils.$('templateThumb');
      const title = Utils.$('templateTitle');
      if (thumb) thumb.removeAttribute('src');
      if (title) title.textContent = '';
      this.els.tplSelection?.classList.add('hidden');
      this.save();
    }

    async submit() {
      if (!Utils.isLoggedIn()) {
        Utils.toast('Log in to generate', 'warn');
        return;
      }

      const isFreeCoverArt = State.entryChoice === 'free-cover-art';

      if (!isFreeCoverArt && State.credits === 0) {
        Events.emit('modal:buy-open-requested');
        return;
      }

      if (State.activeUploads > 0) {
        this.showError('Wait for uploads to finish before generating.');
        return;
      }

      const qty = 1;
      const cost = Config.creditsGen * qty;

      if (!isFreeCoverArt && State.credits < cost) {
        this.showError(`Need ${cost} credits.`);
        return;
      }

      if (isFreeCoverArt) {
        const quota = await this.refreshFreeGenerationStatus({ quiet: true });
        if (quota && Number(quota.totalRemaining || 0) <= 0) {
          this.showError('Your free generations are finished for today. Buy 20 more or wait for the reset.');
          return;
        }
      }

      const isPhotomatic = Config.toolSlug === 'photomatic';
      const selectedTemplate = normalizeTemplateSelection(State.template);
      let generationConcept = buildGenerationConcept(this.els.concept?.value || '', selectedTemplate, DEFAULT_PREMADE_CONCEPT);
      if (isPhotomatic && !generationConcept) {
        generationConcept = `Create a custom family portrait in the ${State.photomaticStyleName || 'selected'} style.`;
      }

      if (!generationConcept) {
        this.showError('Concept required');
        return;
      }
      if (isPhotomatic && !State.photomaticStyleSlug) {
        this.showError('Choose a portrait style first.');
        return;
      }
      if (isPhotomatic && !getService('genUploader')?.uploadedUrls?.length) {
        this.showError('Upload a family photo first.');
        return;
      }

      this.showError(null);
      this.toggleLoading(true);

      const taskData = {
        artist: this.els.artist?.value || '',
        title: this.els.title?.value || '',
        concept: generationConcept,
        qty,
        template: isFreeCoverArt ? null : selectedTemplate,
        templateInput: !isFreeCoverArt && shouldSendTemplateImageInput(selectedTemplate) ? selectedTemplate?.imageUrl || null : null,
        photoInputs: isFreeCoverArt ? [] : [...(getService('genUploader')?.uploadedUrls || [])],
        type: isFreeCoverArt ? 'free-cover-art' : undefined,
        toolSlug: Config.toolSlug,
        nicheSlug: Config.nicheSlug,
        assetFamily: Config.assetFamily,
        assetType: Config.assetType,
        styleSlug: selectedTemplate?.styleId || State.photomaticStyleSlug || '',
        styleName: selectedTemplate?.title || State.photomaticStyleName || '',
        canvasReady: false,
        sizePreset: Config.sizePreset || 'square_1_1',
        imageAspectRatio: Config.imageAspectRatio || '1:1',
      };

      try {
        TaskManager.createTask(taskData);
        Utils.toast('Generation queued');
        Utils.removeStorage(this.autosaveKey);
      } catch (error) {
        this.showError('Failed to queue task');
      } finally {
        this.toggleLoading(false);
      }
    }

    syncFreeModeUi() {
      const isFreeCoverArt = State.entryChoice === 'free-cover-art';
      const uploadBlock = Utils.$('#gen-upload-block');
      uploadBlock?.classList.toggle('hidden', isFreeCoverArt);
      if (isFreeCoverArt) getService('genUploader')?.reset?.();
      this.els.freeStatus?.classList.toggle('hidden', !isFreeCoverArt);
      if (isFreeCoverArt) {
        this.refreshFreeGenerationStatus();
      } else if (this.freeStatusTimer) {
        window.clearTimeout(this.freeStatusTimer);
        this.freeStatusTimer = null;
      }
      const conceptHelper = Utils.$('#concept-helper');
      if (conceptHelper) {
        conceptHelper.textContent = isFreeCoverArt
          ? 'Free daily previews include the Covermatic logo. Paid generations stay logo free.'
          : 'One sentence is enough. Mood + color + vibe.';
      }
    }

    async refreshFreeGenerationStatus({ quiet = false } = {}) {
      if (!Utils.isLoggedIn()) return null;
      try {
        const data = await ApiClient.fetchFreeGenerationStatus();
        this.freeQuota = data;
        this.renderFreeGenerationStatus(data);
        return data;
      } catch (error) {
        if (!quiet) this.renderFreeGenerationStatus(null, 'Could not check free generations.');
        return null;
      }
    }

    renderFreeGenerationStatus(data, fallbackMessage = '') {
      if (!this.els.freeStatus) return;
      const freeRemaining = Number(data?.freeRemaining || 0);
      const purchasedRemaining = Number(data?.purchasedRemaining || 0);
      const totalRemaining = Number(data?.totalRemaining ?? (freeRemaining + purchasedRemaining));
      const resetLabel = data?.nextResetAt ? this.formatResetCountdown(data.nextResetAt) : '';
      if (this.els.freeStatusTitle) {
        this.els.freeStatusTitle.textContent = totalRemaining > 0
          ? `${totalRemaining} free-workflow generation${totalRemaining === 1 ? '' : 's'} available`
          : 'Free generations reset soon';
      }
      if (this.els.freeStatusCopy) {
        this.els.freeStatusCopy.textContent = fallbackMessage || (
          totalRemaining > 0
            ? `${freeRemaining} daily free left${purchasedRemaining ? `, plus ${purchasedRemaining} extra` : ''}. Daily free previews include the logo.`
            : `You can get free generations again ${resetLabel || 'after the daily reset'}, or buy 20 more for 1 credit.`
        );
      }
      this.els.freeReplenish?.classList.toggle('hidden', false);
      this.scheduleFreeCountdown(data?.nextResetAt);
    }

    formatResetCountdown(nextResetAt) {
      const resetMs = Date.parse(nextResetAt);
      if (!Number.isFinite(resetMs)) return '';
      const diff = Math.max(0, resetMs - Date.now());
      const hours = Math.floor(diff / 3600000);
      const minutes = Math.floor((diff % 3600000) / 60000);
      return hours > 0 ? `in ${hours}h ${minutes}m` : `in ${minutes}m`;
    }

    scheduleFreeCountdown(nextResetAt) {
      if (this.freeStatusTimer) window.clearTimeout(this.freeStatusTimer);
      this.freeStatusTimer = null;
      if (!nextResetAt || State.entryChoice !== 'free-cover-art') return;
      this.freeStatusTimer = window.setTimeout(() => {
        if (this.freeQuota) this.renderFreeGenerationStatus(this.freeQuota);
      }, 60000);
    }

    async replenishFreeGenerations() {
      if (!Utils.isLoggedIn() || !this.els.freeReplenish) return;
      this.els.freeReplenish.disabled = true;
      try {
        const data = await ApiClient.replenishFreeGenerations();
        if (typeof data.credits === 'number') State.credits = data.credits;
        this.freeQuota = data;
        this.renderFreeGenerationStatus(data);
        Events.emit('credits:refresh-requested', { fallbackDelta: 0 });
        Utils.toast('20 more generations added.');
      } catch (error) {
        Utils.toast(error?.message || 'Could not buy more generations.', 'error');
      } finally {
        this.els.freeReplenish.disabled = false;
      }
    }

    toggleLoading(on) {
      this.els.loading?.classList.toggle('hidden', !on);
      if (this.els.btn) this.els.btn.disabled = on;
    }

    showError(message) {
      if (!this.els.error) return;
      if (!message) {
        this.els.error.classList.add('hidden');
        return;
      }
      this.els.error.textContent = message;
      this.els.error.classList.remove('hidden');
    }
  }

  runtime.FileUploader = FileUploader;
  runtime.FlowController = FlowController;
  runtime.FormController = FormController;
})();
